import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import { ArrowLeft, Check, X, Trash2, Loader2, Save, ShieldAlert, CalendarDays, Ban, Plus, TrendingUp, KeyRound, Bell, BellRing, Users, Mail, Phone, Search, IdCard, Bike, Lock, MapPin, FileText, Sparkles, BellDot } from "lucide-react";
import { isPushSupported, getCurrentSubscription, subscribeAdminForPush, unsubscribeAdminFromPush, sendPushTest } from "../lib/push";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Bar, BarChart } from "recharts";

const STATUS_COLORS = {
  pending: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40",
  confirmed: "bg-green-500/15 text-green-300 border-green-500/40",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/40",
  blocked: "bg-purple-500/15 text-purple-300 border-purple-500/40",
};

const STATUSES = ["all", "pending", "confirmed", "cancelled", "blocked"];

export default function Admin() {
  const { t } = useI18n();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [filter, setFilter] = useState("all");
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [scooter, setScooter] = useState(null);
  const [fleet, setFleet] = useState({ units_total: 3, full_day: 75, multi_day: 60, half_day: 50, extra_helmet: 15, topcase: 20 });
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);

  const isAdmin = user && user.role === "admin";

  const refresh = async () => {
    setBusy(true);
    try {
      const [b, s, sc] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/stats"),
        api.get("/scooters/niu-nqix-500"),
      ]);
      setBookings(b.data);
      setStats(s.data);
      setScooter(sc.data);
      setFleet({
        units_total: sc.data.units_total,
        full_day: sc.data.pricing?.full_day || 75,
        multi_day: sc.data.pricing?.multi_day || 60,
        half_day: sc.data.pricing?.half_day || 50,
        extra_helmet: sc.data.pricing?.extra_helmet ?? 15,
        topcase: sc.data.pricing?.topcase ?? 20,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    if (!isAdmin) { setBusy(false); return; }
    refresh();
  }, [user, loading, isAdmin, navigate]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Updated");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t.admin.confirm_delete)) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const downloadContract = async (id) => {
    try {
      const res = await api.get(`/admin/bookings/${id}/contract/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contratto-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || t.admin.contract_download_failed);
    }
  };

  const saveFleet = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/scooters/niu-nqix-500`, {
        units_total: parseInt(fleet.units_total, 10),
        pricing: {
          full_day: parseFloat(fleet.full_day),
          multi_day: parseFloat(fleet.multi_day),
          half_day: parseFloat(fleet.half_day),
          extra_helmet: parseFloat(fleet.extra_helmet),
          topcase: parseFloat(fleet.topcase),
        },
      });
      toast.success(t.admin.saved);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  if (loading || (busy && !user)) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-scooter-yellow" /></div>;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#050505] text-white pt-32 pb-16" data-testid="admin-denied">
        <div className="max-w-md mx-auto px-4 text-center">
          <ShieldAlert className="w-12 h-12 mx-auto text-scooter-yellow mb-4" />
          <h1 className="font-heading font-black text-3xl mb-3 tracking-tighter">{t.admin.access_denied}</h1>
          <p className="text-zinc-400 text-sm mb-6">Email: <span className="text-white font-mono">{user?.email}</span></p>
          <button onClick={() => navigate("/")} className="bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase h-12 px-8" data-testid="back-home-btn">Home</button>
        </div>
      </div>
    );
  }

  const filtered = filter === "all" ? bookings : bookings.filter(b => b.status === filter);

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-16" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase font-bold text-zinc-500 hover:text-scooter-yellow mb-6" data-testid="admin-back-btn">
          <ArrowLeft className="w-3 h-3" /> Home
        </button>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-[11px] tracking-[0.35em] uppercase font-bold text-scooter-yellow mb-2">Vico's Scooter</div>
            <h1 className="font-heading font-black text-4xl sm:text-6xl tracking-tighter">{t.admin.title}</h1>
            <p className="text-zinc-400 mt-2">{t.admin.subtitle}</p>
          </div>
          <NotificationBell t={t} onNewBooking={refresh} />
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10" data-testid="admin-stats">
            <StatCard label={t.admin.stats.total} value={stats.total_bookings} />
            <StatCard label={t.admin.stats.pending} value={stats.by_status?.pending?.count || 0} accent="yellow" />
            <StatCard label={t.admin.stats.confirmed} value={stats.by_status?.confirmed?.count || 0} accent="green" />
            <StatCard label={t.admin.stats.cancelled} value={stats.by_status?.cancelled?.count || 0} accent="red" />
            <StatCard label={t.admin.stats.revenue} value={`€${(stats.revenue_estimated || 0).toFixed(0)}`} accent="primary" />
          </div>
        )}

        <div className="flex gap-1 border border-white/10 p-1 mb-6 w-fit flex-wrap">
          {["bookings", "customers", "fleet", "gallery", "calendar", "analytics", "account"].map(k => (
            <button key={k} onClick={() => setTab(k)} className={`h-10 px-5 text-xs font-bold tracking-[0.2em] uppercase transition-colors ${tab === k ? "bg-scooter-yellow text-black" : "text-zinc-400 hover:text-white"}`} data-testid={`admin-tab-${k}`}>
              {t.admin.tabs[k]}
            </button>
          ))}
        </div>

        {tab === "bookings" && (
          <>
            <div className="flex gap-2 mb-4 flex-wrap" data-testid="admin-filter-bar">
              {STATUSES.map(s => (
                <button key={s} onClick={() => setFilter(s)} className={`h-9 px-4 text-[10px] tracking-[0.2em] uppercase font-bold border transition-colors ${filter === s ? "bg-white text-black border-white" : "border-white/20 text-zinc-400 hover:text-white"}`} data-testid={`admin-filter-${s}`}>
                  {s === "all" ? t.admin.filter_all : s} {s !== "all" && stats?.by_status?.[s]?.count !== undefined && `(${stats.by_status[s].count})`}
                </button>
              ))}
            </div>

            {busy ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-scooter-yellow" /></div>
            ) : filtered.length === 0 ? (
              <div className="border border-white/10 p-12 text-center text-zinc-400">{t.admin.empty}</div>
            ) : (
              <div className="border border-white/10 overflow-x-auto" data-testid="admin-bookings-table">
                <div className="hidden md:grid grid-cols-12 gap-3 px-6 py-4 border-b border-white/10 text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">
                  <div className="col-span-2">Cliente</div>
                  <div className="col-span-2">{t.dash.dates}</div>
                  <div className="col-span-1">Qty</div>
                  <div className="col-span-1">Tier</div>
                  <div className="col-span-2">{t.dash.total}</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-3 text-right">Azioni</div>
                </div>
                {filtered.map(b => {
                  const h = b.helmets || {};
                  const helmetParts = ["S","M","L"].filter(s => (h[s]||0) > 0).map(s => `${h[s]}${s}`).join("·");
                  const contractSigned = !!(b.contract && b.contract.signed_at);
                  const aiVerified = !!(b.license?.verification?.verified_at && b.license?.verification?.front?.is_driver_license);
                  return (
                  <div key={b.booking_id} className="grid grid-cols-1 md:grid-cols-12 gap-3 px-6 py-5 border-b border-white/5 hover:bg-white/[0.02] text-sm items-center" data-testid={`admin-row-${b.booking_id}`}>
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-bold text-white truncate">{b.customer_name}</span>
                        {aiVerified && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] tracking-wider uppercase font-bold border border-scooter-yellow/50 bg-scooter-yellow/10 text-scooter-yellow" title={t.admin.ai_verified} data-testid={`ai-verified-${b.booking_id}`}>
                            <Sparkles className="w-2.5 h-2.5" />{t.admin.ai_verified}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 truncate">{b.customer_email}</div>
                      {b.customer_phone && <div className="text-xs text-zinc-600">{b.customer_phone}</div>}
                      {contractSigned && (
                        <div className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] tracking-wider uppercase font-bold border border-green-500/40 bg-green-500/10 text-green-300" title={t.admin.contract_signed} data-testid={`contract-signed-${b.booking_id}`}>
                          <FileText className="w-2.5 h-2.5" />{t.admin.contract_signed}
                        </div>
                      )}
                    </div>
                    <div className="md:col-span-2 text-zinc-300">{b.start_date}<br/><span className="text-zinc-500">→ {b.end_date}</span>
                      {b.pickup_location?.name && <div className="text-[10px] text-scooter-yellow mt-1 flex items-center gap-1" data-testid={`row-pickup-${b.booking_id}`}><MapPin className="w-3 h-3 flex-shrink-0" />{b.pickup_location.name}</div>}
                    </div>
                    <div className="md:col-span-1">
                      <div className="font-heading font-bold text-xl">{b.quantity}×</div>
                      {(b.unit_names?.length > 0) && <div className="text-[10px] text-scooter-yellow tracking-wide" data-testid={`row-units-${b.booking_id}`}>{b.unit_names.join(", ")}</div>}
                    </div>
                    <div className="md:col-span-1 text-xs">
                      <div className="uppercase tracking-wider text-zinc-400">{b.pricing_tier.replace("_", " ")}</div>
                      {helmetParts && <div className="text-[10px] text-zinc-500 mt-1" title={t.admin.helmets}>{t.admin.helmets}: {helmetParts}</div>}
                      {(b.topcases || 0) > 0 && <div className="text-[10px] text-zinc-500 mt-1" title={t.admin.topcase}>{t.admin.topcase}: {b.topcases}×</div>}
                    </div>
                    <div className="md:col-span-2 text-scooter-yellow font-bold text-lg">€{b.total_price.toFixed(2)}</div>
                    <div className="md:col-span-1"><span className={`inline-block px-2 py-1 text-[9px] tracking-[0.2em] uppercase font-bold border ${STATUS_COLORS[b.status] || ""}`}>{b.status}</span></div>
                    <div className="md:col-span-3 flex gap-2 md:justify-end flex-wrap">
                      {contractSigned && <button onClick={() => downloadContract(b.booking_id)} title={t.admin.download_contract} className="w-9 h-9 border border-scooter-yellow/50 text-scooter-yellow hover:bg-scooter-yellow/10 flex items-center justify-center" data-testid={`download-contract-${b.booking_id}`}><FileText className="w-4 h-4" /></button>}
                      {b.status !== "confirmed" && <button onClick={() => changeStatus(b.booking_id, "confirmed")} title={t.admin.confirm} className="w-9 h-9 border border-green-500/40 text-green-300 hover:bg-green-500/20 flex items-center justify-center" data-testid={`confirm-${b.booking_id}`}><Check className="w-4 h-4" /></button>}
                      {b.status !== "cancelled" && <button onClick={() => changeStatus(b.booking_id, "cancelled")} title={t.admin.cancel} className="w-9 h-9 border border-red-500/40 text-red-300 hover:bg-red-500/20 flex items-center justify-center" data-testid={`cancel-${b.booking_id}`}><X className="w-4 h-4" /></button>}
                      <button onClick={() => remove(b.booking_id)} title={t.admin.delete} className="w-9 h-9 border border-white/10 text-zinc-400 hover:bg-white/5 hover:text-red-400 flex items-center justify-center" data-testid={`delete-${b.booking_id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {tab === "fleet" && scooter && (
          <div className="space-y-8">
          <div className="max-w-xl border border-white/10 p-8" data-testid="admin-fleet-panel">
            <div className="flex items-center gap-4 mb-8">
              <img src={scooter.image} alt={scooter.model} className="w-20 h-20 object-cover border border-white/10" />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow">{scooter.brand}</div>
                <div className="font-heading font-black text-2xl tracking-tighter">{scooter.model}</div>
                <div className="text-xs text-zinc-500 mt-1">ID: {scooter.scooter_id}</div>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.fleet_units}</Label>
                <Input type="number" min={1} value={fleet.units_total} onChange={(e) => setFleet(f => ({...f, units_total: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none text-white text-2xl font-heading font-bold h-14 focus-visible:ring-scooter-yellow" data-testid="fleet-units-input" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow">{t.admin.fleet_full_day}</Label>
                  <Input type="number" step="0.01" value={fleet.full_day} onChange={(e) => setFleet(f => ({...f, full_day: e.target.value}))} className="mt-2 bg-transparent border-scooter-yellow/40 rounded-none text-white text-xl font-bold h-12 focus-visible:ring-scooter-yellow" data-testid="fleet-fullday-input" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.fleet_half_day}</Label>
                  <Input type="number" step="0.01" value={fleet.half_day} onChange={(e) => setFleet(f => ({...f, half_day: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none text-white text-xl font-bold h-12 focus-visible:ring-scooter-yellow" data-testid="fleet-halfday-input" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.fleet_multi_day}</Label>
                  <Input type="number" step="0.01" value={fleet.multi_day} onChange={(e) => setFleet(f => ({...f, multi_day: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none text-white text-xl font-bold h-12 focus-visible:ring-scooter-yellow" data-testid="fleet-multiday-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                <div>
                  <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.fleet_extra_helmet}</Label>
                  <Input type="number" step="0.01" value={fleet.extra_helmet} onChange={(e) => setFleet(f => ({...f, extra_helmet: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none text-white text-xl font-bold h-12 focus-visible:ring-scooter-yellow" data-testid="fleet-helmet-input" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.fleet_topcase}</Label>
                  <Input type="number" step="0.01" value={fleet.topcase} onChange={(e) => setFleet(f => ({...f, topcase: e.target.value}))} className="mt-2 bg-transparent border-white/20 rounded-none text-white text-xl font-bold h-12 focus-visible:ring-scooter-yellow" data-testid="fleet-topcase-input" />
                </div>
              </div>
              <button onClick={saveFleet} disabled={saving} className="w-full h-12 bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover disabled:opacity-50 flex items-center justify-center gap-2 mt-4" data-testid="fleet-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t.admin.save}
              </button>
            </div>
          </div>
          <AdminUnitsPanel t={t} />
          <AdminLocationsPanel t={t} />
          </div>
        )}
        {tab === "customers" && (
          <AdminCustomersTab t={t} bookings={bookings} />
        )}
        {tab === "calendar" && (
          <AdminCalendarTab t={t} scooter={scooter} onChange={refresh} />
        )}
        {tab === "analytics" && (
          <AdminAnalyticsTab t={t} />
        )}
        {tab === "gallery" && (
          <AdminGalleryPanel t={t} />
        )}
        {tab === "account" && (
          <AdminAccountTab t={t} />
        )}
      </div>
    </div>
  );
}

function AdminLocationsPanel({ t }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", address: "", notes: "", customer_provides_address: false });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/locations").then(r => setLocations(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim()) return toast.error(t.admin.loc_name_required || "Nome del punto obbligatorio");
    setSaving(true);
    try {
      await api.post("/admin/locations", form);
      setForm({ name: "", address: "", notes: "", customer_provides_address: false });
      toast.success(t.admin.loc_added || "Punto aggiunto");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t.admin.loc_delete_confirm || "Rimuovere questo punto di ritiro?")) return;
    try {
      await api.delete(`/admin/locations/${id}`);
      toast.success(t.admin.loc_removed || "Punto rimosso");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="max-w-3xl border border-white/10 p-8" data-testid="admin-locations-panel">
      <div className="flex items-center gap-2 mb-2">
        <MapPin className="w-5 h-5 text-scooter-yellow" />
        <h3 className="font-heading font-black text-2xl tracking-tighter">{t.admin.loc_title || "Punti di ritiro e riconsegna"}</h3>
      </div>
      <p className="text-xs text-zinc-500 mb-6">{t.admin.loc_subtitle || "Il cliente sceglie uno di questi punti durante la prenotazione. Ritiro e riconsegna avvengono nello stesso punto."}</p>

      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-scooter-yellow" />
      ) : (
        <div className="space-y-3 mb-8">
          {locations.length === 0 && <div className="text-sm text-zinc-500 border border-white/10 p-4">{t.admin.loc_empty || "Nessun punto configurato — i clienti non vedranno la scelta."}</div>}
          {locations.map(l => (
            <div key={l.location_id} className="flex items-start justify-between gap-4 border border-white/10 p-4" data-testid={`location-row-${l.location_id}`}>
              <div>
                <div className="font-heading font-bold text-white flex items-center gap-2">{l.name}
                  {l.customer_provides_address && (
                    <span className="inline-flex items-center gap-1 text-[9px] tracking-wider uppercase font-bold px-1.5 py-0.5 border border-scooter-yellow/50 text-scooter-yellow bg-scooter-yellow/10" data-testid={`loc-customer-badge-${l.location_id}`}>
                      {t.admin.loc_customer_badge || "Indirizzo dal cliente"}
                    </span>
                  )}
                </div>
                {l.address && <div className="text-xs text-zinc-400 mt-0.5">{l.address}</div>}
                {l.notes && <div className="text-[11px] text-zinc-500 mt-0.5">{l.notes}</div>}
              </div>
              <button onClick={() => remove(l.location_id)} title={t.admin.delete} className="w-9 h-9 border border-white/10 text-zinc-400 hover:bg-white/5 hover:text-red-400 flex items-center justify-center flex-shrink-0" data-testid={`location-delete-${l.location_id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-white/10 pt-6 space-y-3">
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow">{t.admin.loc_add_title || "Aggiungi punto"}</div>
        <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t.admin.loc_name || "Nome (es. Camping Sass Dlacia)"} className="bg-transparent border-white/20 rounded-none text-white h-11 focus-visible:ring-scooter-yellow" data-testid="location-name-input" />
        <Input
          value={form.customer_provides_address ? "" : form.address}
          onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
          placeholder={form.customer_provides_address ? (t.admin.loc_address_locked || "Il cliente inserirà il nome dell'hotel al momento della prenotazione") : (t.admin.loc_address || "Indirizzo")}
          disabled={form.customer_provides_address}
          className="bg-transparent border-white/20 rounded-none text-white h-11 focus-visible:ring-scooter-yellow disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="location-address-input"
        />
        <label className="flex items-start gap-3 cursor-pointer select-none py-1" data-testid="location-customer-toggle">
          <input
            type="checkbox"
            checked={!!form.customer_provides_address}
            onChange={e => setForm(f => ({ ...f, customer_provides_address: e.target.checked, address: e.target.checked ? "" : f.address }))}
            className="mt-1 h-4 w-4 accent-scooter-yellow"
          />
          <span className="text-xs text-zinc-300 leading-relaxed">
            <span className="font-bold text-white">{t.admin.loc_customer_label || "Il cliente sceglie l'indirizzo"}</span>
            <br />
            <span className="text-zinc-500 text-[11px]">{t.admin.loc_customer_hint || "Usa questa opzione per punti generici (es. \"Il tuo hotel\") dove sarà il cliente a specificare il nome della struttura in fase di prenotazione."}</span>
          </span>
        </label>
        <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder={t.admin.loc_notes || "Orari / note (es. ritiro dalle 9:00)"} className="bg-transparent border-white/20 rounded-none text-white h-11 focus-visible:ring-scooter-yellow" data-testid="location-notes-input" />
        <button onClick={add} disabled={saving} className="h-11 px-6 bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover disabled:opacity-50 flex items-center gap-2" data-testid="location-add-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {t.admin.loc_add || "Aggiungi"}
        </button>
      </div>
    </div>
  );
}

function AdminUnitsPanel({ t }) {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    api.get("/admin/units")
      .then(r => setUnits(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "Error"))
      .finally(() => setLoading(false));
  }, []);

  const setField = (uid, field, value) => setUnits(us => us.map(u => u.unit_id === uid ? { ...u, [field]: value } : u));

  const save = async (u) => {
    if (!u.public_name?.trim()) return toast.error(t.admin.unit_name);
    setSavingId(u.unit_id);
    try {
      await api.patch(`/admin/units/${u.unit_id}`, {
        public_name: u.public_name.trim(),
        plate: u.plate || "",
        insurance_code: u.insurance_code || "",
      });
      toast.success(t.admin.unit_saved);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSavingId(null); }
  };

  return (
    <div className="max-w-4xl border border-white/10 p-8" data-testid="admin-units-panel">
      <div className="flex items-center gap-2 mb-1">
        <Bike className="w-4 h-4 text-scooter-yellow" />
        <h2 className="font-heading font-black text-xl tracking-tight">{t.admin.units_title}</h2>
      </div>
      <p className="text-xs text-zinc-400 mb-6 flex items-center gap-1.5"><Lock className="w-3 h-3 text-scooter-yellow" /> {t.admin.units_sub}</p>
      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-scooter-yellow" /></div>
      ) : (
        <div className="space-y-3">
          {units.map(u => (
            <div key={u.unit_id} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end border border-white/10 p-4" data-testid={`unit-row-${u.unit_id}`}>
              <div className="md:col-span-4">
                <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow">{t.admin.unit_name}</Label>
                <Input value={u.public_name || ""} onChange={e => setField(u.unit_id, "public_name", e.target.value)} className="mt-2 bg-transparent border-scooter-yellow/40 rounded-none text-white font-heading font-bold text-lg h-11 focus-visible:ring-scooter-yellow" data-testid={`unit-name-${u.unit_id}`} />
              </div>
              <div className="md:col-span-3">
                <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.unit_plate}</Label>
                <Input value={u.plate || ""} onChange={e => setField(u.unit_id, "plate", e.target.value)} placeholder="—" className="mt-2 bg-transparent border-white/20 rounded-none text-white font-mono h-11 focus-visible:ring-scooter-yellow" data-testid={`unit-plate-${u.unit_id}`} />
              </div>
              <div className="md:col-span-3">
                <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.unit_insurance}</Label>
                <Input value={u.insurance_code || ""} onChange={e => setField(u.unit_id, "insurance_code", e.target.value)} placeholder="—" className="mt-2 bg-transparent border-white/20 rounded-none text-white font-mono h-11 focus-visible:ring-scooter-yellow" data-testid={`unit-insurance-${u.unit_id}`} />
              </div>
              <div className="md:col-span-2">
                <button onClick={() => save(u)} disabled={savingId === u.unit_id} className="w-full h-11 bg-scooter-yellow text-black font-bold text-[10px] tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover disabled:opacity-50 flex items-center justify-center gap-2" data-testid={`unit-save-${u.unit_id}`}>
                  {savingId === u.unit_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  {t.admin.save}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminAnalyticsTab({ t }) {
  const [data, setData] = useState(null);
  const [visits, setVisits] = useState(null);
  const [gran, setGran] = useState("month");
  const [range, setRange] = useState(12);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/revenue`, { params: { granularity: gran, periods: range, offset } })
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "Error"))
      .finally(() => setLoading(false));
  }, [gran, range, offset]);
  useEffect(() => {
    api.get("/admin/analytics/visits").then(r => setVisits(r.data)).catch(() => {});
  }, []);

  const chartData = (data?.series || []).map(s => {
    const label = s.month || s.period || "";
    let short = label;
    if (gran === "month") short = label.slice(2).replace("-", "/");  // "26/02"
    else if (gran === "week") short = "W" + label.slice(-2);  // "W25"
    else short = label.slice(5);  // "07-04"
    return { label: short, revenue: Math.round(s.revenue), bookings: s.bookings };
  });
  const totalRev = data?.total_revenue || 0;
  const totalBookings = chartData.reduce((a, b) => a + b.bookings, 0);
  const avgTicket = totalBookings ? Math.round(totalRev / totalBookings) : 0;

  const ranges = gran === "month" ? [6, 12, 24] : gran === "week" ? [4, 12, 26] : [7, 30, 90];
  useEffect(() => {
    // Snap range to a valid value when gran changes
    if (!ranges.includes(range)) setRange(ranges[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gran]);

  return (
    <div data-testid="admin-analytics-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-scooter-yellow" />
          <h2 className="font-heading font-black text-xl tracking-tight">{t.admin.an_title}</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex gap-1 border border-white/10 p-1" data-testid="offset-switcher">
            <button onClick={() => setOffset(o => o - Math.max(1, Math.floor(range / 2)))} className="h-8 px-3 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400 hover:text-white" data-testid="offset-prev" title="Periodo precedente">◄</button>
            <button onClick={() => setOffset(0)} disabled={offset === 0} className={`h-8 px-3 text-[10px] font-bold tracking-[0.2em] uppercase ${offset === 0 ? "bg-scooter-yellow text-black" : "text-zinc-400 hover:text-white"}`} data-testid="offset-now">{t.admin.gran_now}</button>
            <button onClick={() => setOffset(o => o + Math.max(1, Math.floor(range / 2)))} className="h-8 px-3 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400 hover:text-white" data-testid="offset-next" title="Periodo successivo">►</button>
          </div>
          <div className="flex gap-1 border border-white/10 p-1" data-testid="gran-switcher">
            {[
              { k: "month", label: t.admin.gran_month },
              { k: "week", label: t.admin.gran_week },
              { k: "day", label: t.admin.gran_day },
            ].map(g => (
              <button key={g.k} onClick={() => { setGran(g.k); setOffset(0); }} className={`h-8 px-4 text-[10px] font-bold tracking-[0.2em] uppercase ${gran === g.k ? "bg-scooter-yellow text-black" : "text-zinc-400 hover:text-white"}`} data-testid={`gran-${g.k}`}>{g.label}</button>
            ))}
          </div>
          <div className="flex gap-1 border border-white/10 p-1">
            {ranges.map(n => (
              <button key={n} onClick={() => setRange(n)} className={`h-8 px-4 text-[10px] font-bold tracking-[0.2em] uppercase ${range === n ? "bg-scooter-yellow text-black" : "text-zinc-400 hover:text-white"}`} data-testid={`range-${n}`}>{n}{gran[0]}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="border border-scooter-yellow/30 bg-scooter-yellow/5 p-5" data-testid="kpi-revenue">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400 mb-2">{t.admin.an_total_rev}</div>
          <div className="font-heading font-black text-4xl text-scooter-yellow tracking-tighter">€{totalRev.toLocaleString('it-IT', { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="border border-white/10 p-5" data-testid="kpi-bookings">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400 mb-2">{t.admin.an_total_bk}</div>
          <div className="font-heading font-black text-4xl text-white tracking-tighter">{totalBookings}</div>
        </div>
        <div className="border border-white/10 p-5" data-testid="kpi-avg">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400 mb-2">{t.admin.an_avg_ticket}</div>
          <div className="font-heading font-black text-4xl text-white tracking-tighter">€{avgTicket}</div>
        </div>
      </div>

      {/* Visite / click sul sito */}
      {visits && (
        <div className="border border-white/10 p-5 mb-6" data-testid="visits-panel">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-scooter-yellow" />
            <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.visits_title || "Visite al sito"}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="border border-white/10 p-4" data-testid="visits-today">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400 mb-1">{t.admin.visits_today || "Oggi"}</div>
              <div className="font-heading font-black text-3xl text-white tracking-tighter">{visits.today}</div>
            </div>
            <div className="border border-white/10 p-4" data-testid="visits-7d">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400 mb-1">{t.admin.visits_7d || "Ultimi 7 giorni"}</div>
              <div className="font-heading font-black text-3xl text-white tracking-tighter">{visits.last_7d.visits}</div>
              <div className="text-[11px] text-zinc-500 mt-1">{visits.last_7d.unique} {t.admin.visits_unique || "unici"}</div>
            </div>
            <div className="border border-white/10 p-4" data-testid="visits-30d">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-400 mb-1">{t.admin.visits_30d || "Ultimi 30 giorni"}</div>
              <div className="font-heading font-black text-3xl text-white tracking-tighter">{visits.last_30d.visits}</div>
              <div className="text-[11px] text-zinc-500 mt-1">{visits.last_30d.unique} {t.admin.visits_unique || "unici"}</div>
            </div>
            <div className="border border-scooter-yellow/30 bg-scooter-yellow/5 p-4" data-testid="visits-book-clicks">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow/80 mb-1">{t.admin.visits_book_clicks || "Click su Prenota"}</div>
              <div className="font-heading font-black text-3xl text-scooter-yellow tracking-tighter">{visits.totals.clicks_book}</div>
              <div className="text-[11px] text-zinc-500 mt-1">{t.admin.visits_all || "totali"}: {visits.totals.visits_all} · {visits.totals.unique_all} {t.admin.visits_unique || "unici"}</div>
            </div>
          </div>
          <div className="h-40" data-testid="visits-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={visits.by_day.map(d => ({ label: d.date.slice(5), visits: d.hits, unique: d.unique }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="label" stroke="#71717a" fontSize={10} />
                <YAxis stroke="#71717a" fontSize={10} allowDecimals={false} />
                <RTooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, fontSize: 12 }} labelStyle={{ color: "#fff" }} />
                <Line type="monotone" dataKey="visits" name={t.admin.visits_visits || "Visite"} stroke="#FFE600" strokeWidth={2} dot={{ r: 3, fill: "#FFE600" }} />
                <Line type="monotone" dataKey="unique" name={t.admin.visits_unique || "Unici"} stroke="#a1a1aa" strokeWidth={2} dot={{ r: 2, fill: "#a1a1aa" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {visits.top_referrers?.length > 0 && (
            <div className="mt-5">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500 mb-2">{t.admin.visits_referrers || "Sorgenti principali (30 gg)"}</div>
              <div className="space-y-1" data-testid="visits-referrers">
                {visits.top_referrers.map(r => (
                  <div key={r.referrer} className="flex items-center justify-between text-xs border-b border-white/5 py-1">
                    <span className="text-zinc-300 truncate max-w-[80%]">{r.referrer}</span>
                    <span className="text-scooter-yellow font-bold">{r.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="border border-white/10 p-5 mb-6">
        <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-zinc-500 mb-4">{t.admin.an_revenue_chart}</div>
        {loading ? (
          <div className="h-72 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-scooter-yellow" /></div>
        ) : (
          <div className="h-72" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="label" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} tickFormatter={v => `€${v}`} />
                <RTooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, fontSize: 12 }} labelStyle={{ color: "#fff" }} formatter={(v, n) => n === "revenue" ? [`€${v}`, "Revenue"] : [v, n]} />
                <Line type="monotone" dataKey="revenue" stroke="#FFE600" strokeWidth={3} dot={{ r: 4, fill: "#FFE600" }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="border border-white/10 p-5">
        <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-zinc-500 mb-4">{t.admin.an_bookings_chart}</div>
        {loading ? (
          <div className="h-56 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-scooter-yellow" /></div>
        ) : (
          <div className="h-56" data-testid="bookings-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="label" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
                <RTooltip contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, fontSize: 12 }} labelStyle={{ color: "#fff" }} />
                <Bar dataKey="bookings" fill="#FFE600" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminAccountTab({ t }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.current_password || !form.new_password) return toast.error(t.admin.pw_all_fields);
    if (form.new_password.length < 6) return toast.error(t.admin.pw_min);
    if (form.new_password !== form.confirm) return toast.error(t.admin.pw_mismatch);
    setSaving(true);
    try {
      await api.post(`/auth/change-password`, {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success(t.admin.pw_changed);
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg" data-testid="admin-account-panel">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="w-4 h-4 text-scooter-yellow" />
        <h2 className="font-heading font-black text-xl tracking-tight">{t.admin.pw_title}</h2>
      </div>
      <p className="text-xs text-zinc-400 mb-6">{t.admin.pw_sub}</p>

      <div className="border border-white/10 p-6 space-y-4">
        <div>
          <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.pw_current}</Label>
          <Input type="password" value={form.current_password} onChange={e => setForm(f => ({ ...f, current_password: e.target.value }))} className="mt-2 bg-transparent border-white/20 rounded-none text-white h-12" autoComplete="current-password" data-testid="pw-current" />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.pw_new}</Label>
          <Input type="password" value={form.new_password} onChange={e => setForm(f => ({ ...f, new_password: e.target.value }))} className="mt-2 bg-transparent border-white/20 rounded-none text-white h-12" autoComplete="new-password" data-testid="pw-new" />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.pw_confirm}</Label>
          <Input type="password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} className="mt-2 bg-transparent border-white/20 rounded-none text-white h-12" autoComplete="new-password" data-testid="pw-confirm" />
        </div>
        <button onClick={submit} disabled={saving} className="w-full h-12 bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover disabled:opacity-50 flex items-center justify-center gap-2" data-testid="pw-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t.admin.pw_save}
        </button>
      </div>

      <PushNotificationsPanel t={t} />
    </div>
  );
}

function AdminCalendarTab({ t, scooter, onChange }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const today = new Date();
  const in60 = new Date(); in60.setDate(today.getDate() + 60);
  const iso = (d) => d.toISOString().slice(0, 10);
  const [blockForm, setBlockForm] = useState({
    start_date: iso(today),
    end_date: iso(today),
    quantity: 1,
    notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const start = iso(today);
      const end = iso(in60);
      const { data } = await api.get(`/admin/calendar`, { params: { start, end, scooter_id: "niu-nqix-500" } });
      setDays(data.days || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const createBlock = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/blocks`, {
        scooter_id: "niu-nqix-500",
        start_date: blockForm.start_date,
        end_date: blockForm.end_date,
        quantity: parseInt(blockForm.quantity, 10),
        notes: blockForm.notes,
      });
      toast.success(t.admin.block_created);
      setBlockForm({ ...blockForm, notes: "" });
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-6" data-testid="admin-calendar-panel">
      <div className="lg:col-span-8 border border-white/10 p-6">
        <div className="flex items-center gap-2 mb-1">
          <CalendarDays className="w-4 h-4 text-scooter-yellow" />
          <h2 className="font-heading font-black text-xl tracking-tight">{t.admin.cal_title}</h2>
        </div>
        <p className="text-xs text-zinc-400 mb-6">{t.admin.cal_sub}</p>
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-scooter-yellow" /></div>
        ) : (
          <div className="grid grid-cols-7 gap-1.5" data-testid="calendar-grid">
            {days.map(d => {
              const isFull = d.available === 0;
              const hasBlocks = (d.blocked || 0) > 0;
              const dt = new Date(d.date);
              return (
                <div
                  key={d.date}
                  className={`aspect-square border p-1.5 flex flex-col justify-between text-[10px] ${isFull ? "border-red-500/40 bg-red-500/5" : hasBlocks ? "border-purple-500/40 bg-purple-500/5" : "border-white/10"}`}
                  title={`${d.date} — ${d.available} liberi, ${d.booked} prenotati${hasBlocks ? `, ${d.blocked} bloccati` : ""}`}
                  data-testid={`calday-${d.date}`}
                >
                  <div className="flex justify-between">
                    <span className="text-zinc-500 font-bold">{dt.getDate()}</span>
                    <span className="text-[8px] text-zinc-600 uppercase">{dt.toLocaleDateString('en', { weekday: 'short' }).slice(0, 2)}</span>
                  </div>
                  <div className={`font-heading font-black text-lg text-center leading-none ${isFull ? "text-red-400" : "text-scooter-yellow"}`}>
                    {d.available}/{d.total}
                  </div>
                  {hasBlocks && <div className="text-[8px] text-purple-300 text-center flex items-center justify-center gap-0.5"><Ban className="w-2 h-2" />{d.blocked}</div>}
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-6 flex flex-wrap gap-4 text-[10px] text-zinc-500">
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 border border-white/20" /> {t.admin.cal_available}</div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 border border-red-500/40 bg-red-500/10" /> Full</div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 border border-purple-500/40 bg-purple-500/10" /> {t.admin.cal_blocked}</div>
        </div>
      </div>

      <div className="lg:col-span-4 border border-purple-500/30 bg-purple-500/5 p-6" data-testid="block-form">
        <div className="flex items-center gap-2 mb-1">
          <Ban className="w-4 h-4 text-purple-300" />
          <h3 className="font-heading font-black text-lg tracking-tight text-purple-100">{t.admin.block_title}</h3>
        </div>
        <p className="text-xs text-zinc-400 mb-5">{t.admin.block_sub}</p>
        <div className="space-y-4">
          <div>
            <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.block_start}</Label>
            <Input type="date" value={blockForm.start_date} onChange={e => setBlockForm(f => ({ ...f, start_date: e.target.value }))} className="mt-2 bg-transparent border-white/20 rounded-none text-white" data-testid="block-start" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.block_end}</Label>
            <Input type="date" value={blockForm.end_date} onChange={e => setBlockForm(f => ({ ...f, end_date: e.target.value }))} className="mt-2 bg-transparent border-white/20 rounded-none text-white" data-testid="block-end" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.block_qty}</Label>
            <Input type="number" min={1} max={scooter?.units_total || 1} value={blockForm.quantity} onChange={e => setBlockForm(f => ({ ...f, quantity: e.target.value }))} className="mt-2 bg-transparent border-white/20 rounded-none text-white text-xl font-bold h-12" data-testid="block-qty" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.block_notes}</Label>
            <Textarea value={blockForm.notes} onChange={e => setBlockForm(f => ({ ...f, notes: e.target.value }))} className="mt-2 bg-transparent border-white/20 rounded-none text-white" data-testid="block-notes" />
          </div>
          <button onClick={createBlock} disabled={saving} className="w-full h-11 bg-purple-500 text-white font-bold text-xs tracking-[0.2em] uppercase hover:bg-purple-400 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="block-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {t.admin.block_btn}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  const accentMap = {
    yellow: "border-yellow-500/40 text-yellow-300",
    green: "border-green-500/40 text-green-300",
    red: "border-red-500/40 text-red-300",
    primary: "border-scooter-yellow/40 text-scooter-yellow",
  };
  return (
    <div className={`bg-[#0a0a0a] border ${accentMap[accent] || "border-white/10 text-white"} p-5`}>
      <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500 mb-2">{label}</div>
      <div className="font-heading font-black text-3xl tracking-tighter">{value}</div>
    </div>
  );
}

function AdminCustomersTab({ t, bookings }) {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [licenseView, setLicenseView] = useState(null); // {data, loading}
  const openLicense = async (bookingId) => {
    setLicenseView({ loading: true, bookingId });
    try {
      const { data } = await api.get(`/admin/bookings/${bookingId}/license/full`);
      setLicenseView({ loading: false, data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
      setLicenseView(null);
    }
  };
  // Aggregate: group all non-blocked bookings by email
  const customers = useMemo(() => {
    const map = new Map();
    (bookings || []).filter(b => b.status !== "blocked").forEach(b => {
      const key = (b.customer_email || "").toLowerCase();
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, { email: key, name: b.customer_name, phone: b.customer_phone || "", bookings: [], total_paid: 0, first_booking: b.created_at, last_booking: b.created_at });
      }
      const c = map.get(key);
      c.bookings.push(b);
      c.total_paid += (b.status === "confirmed" || b.status === "pending") ? (b.total_price || 0) : 0;
      if (b.created_at < c.first_booking) c.first_booking = b.created_at;
      if (b.created_at > c.last_booking) c.last_booking = b.created_at;
      if (b.customer_phone && !c.phone) c.phone = b.customer_phone;
    });
    return Array.from(map.values()).sort((a, b) => b.last_booking.localeCompare(a.last_booking));
  }, [bookings]);

  const filtered = q.trim() === "" ? customers : customers.filter(c =>
    c.name?.toLowerCase().includes(q.toLowerCase()) ||
    c.email.includes(q.toLowerCase()) ||
    (c.phone || "").includes(q)
  );

  const exportCsv = () => {
    const rows = [["Nome", "Email", "Telefono", "Prenotazioni", "Totale €", "Prima", "Ultima"]];
    customers.forEach(c => {
      rows.push([c.name || "", c.email, c.phone, c.bookings.length, c.total_paid.toFixed(2), c.first_booking?.slice(0, 10) || "", c.last_booking?.slice(0, 10) || ""]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `vicos-scooter-clienti-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-customers-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-scooter-yellow" />
          <h2 className="font-heading font-black text-xl tracking-tight">{t.admin.cust_title}</h2>
          <span className="text-zinc-500 text-sm ml-2" data-testid="cust-count">({filtered.length})</span>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search className="w-3 h-3 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text" placeholder={t.admin.cust_search}
              value={q} onChange={e => setQ(e.target.value)}
              className="bg-transparent border border-white/15 h-10 pl-9 pr-4 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-scooter-yellow w-64"
              data-testid="cust-search"
            />
          </div>
          <button onClick={exportCsv} className="h-10 px-4 border border-white/15 text-white text-[10px] font-bold tracking-[0.2em] uppercase hover:border-scooter-yellow flex items-center gap-2" data-testid="cust-export">
            <Save className="w-3 h-3" /> {t.admin.cust_export}
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-white/10 p-10 text-center text-zinc-500 text-sm" data-testid="cust-empty">{t.admin.cust_empty}</div>
      ) : (
        <div className="space-y-2" data-testid="customers-list">
          {filtered.map(c => (
            <div key={c.email} className="border border-white/10 hover:border-white/20 transition-colors" data-testid={`customer-${c.email}`}>
              <div className="p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center cursor-pointer" onClick={() => setExpanded(e => e === c.email ? null : c.email)}>
                <div className="md:col-span-4">
                  <div className="font-bold text-white text-sm truncate">{c.name}</div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5 truncate"><Mail className="w-3 h-3" /> <a href={`mailto:${c.email}`} onClick={e=>e.stopPropagation()} className="hover:text-scooter-yellow truncate" data-testid={`cust-email-${c.email}`}>{c.email}</a></div>
                </div>
                <div className="md:col-span-3">
                  {c.phone ? (
                    <a href={`tel:${c.phone}`} onClick={e=>e.stopPropagation()} className="text-xs text-zinc-300 hover:text-scooter-yellow flex items-center gap-1.5" data-testid={`cust-phone-${c.email}`}>
                      <Phone className="w-3 h-3" /> {c.phone}
                    </a>
                  ) : <span className="text-[11px] text-zinc-600">—</span>}
                </div>
                <div className="md:col-span-2 text-center">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t.admin.cust_bookings}</div>
                  <div className="font-heading font-bold text-lg text-white">{c.bookings.length}</div>
                </div>
                <div className="md:col-span-2 text-center">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{t.admin.cust_total}</div>
                  <div className="font-heading font-bold text-lg text-scooter-yellow">€{c.total_paid.toFixed(0)}</div>
                </div>
                <div className="md:col-span-1 text-right text-[10px] text-zinc-500 uppercase tracking-wider">
                  {c.last_booking?.slice(0, 10)}
                </div>
              </div>
              {expanded === c.email && (
                <div className="border-t border-white/10 p-4 bg-white/[0.02] space-y-2" data-testid={`cust-details-${c.email}`}>
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500 mb-2">{t.admin.cust_all_bookings}</div>
                  {c.bookings.map(b => (
                    <div key={b.booking_id} className="grid grid-cols-1 md:grid-cols-7 gap-2 py-2 border-b border-white/5 text-xs items-center">
                      <div className="md:col-span-2">
                        <div className="text-zinc-400 text-[11px]">#{b.booking_id.replace("bk_", "")}</div>
                        <div className="text-white">{b.start_date} → {b.end_date}</div>
                      </div>
                      <div className="text-zinc-400">{b.quantity}×{(b.unit_names?.length > 0) ? ` (${b.unit_names.join(", ")})` : ""} · {b.pricing_tier.replace("_", " ")}</div>
                      <div className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 border w-fit ${STATUS_COLORS[b.status] || "border-white/10 text-white"}`}>{b.status}</div>
                      <div className="text-white text-sm font-bold">€{(b.total_price || 0).toFixed(0)}</div>
                      <div className="text-zinc-500 text-[10px]">{b.created_at?.slice(0, 10)}</div>
                      <div>
                        {b.license?.uploaded_at && (
                          <button onClick={(e) => { e.stopPropagation(); openLicense(b.booking_id); }} className="text-[10px] uppercase tracking-wider font-bold text-scooter-yellow hover:underline flex items-center gap-1" data-testid={`view-license-${b.booking_id}`}>
                            <IdCard className="w-3 h-3" /> {t.admin.view_license}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {c.bookings.some(b => b.notes) && (
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-500 mb-2">{t.admin.cust_notes}</div>
                      {c.bookings.filter(b => b.notes).map(b => (
                        <div key={b.booking_id} className="text-xs text-zinc-300 italic">&ldquo;{b.notes}&rdquo; — #{b.booking_id.replace("bk_", "")}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {licenseView && <LicenseViewModal view={licenseView} onClose={() => setLicenseView(null)} t={t} onDeleted={() => window.location.reload()} />}
    </div>
  );
}

function LicenseViewModal({ view, onClose, t, onDeleted }) {
  const l = view.data?.license;
  const [deleting, setDeleting] = useState(false);
  // Countdown to auto-delete (7 days from uploaded_at)
  const daysLeft = React.useMemo(() => {
    if (!l?.uploaded_at) return null;
    const uploaded = new Date(l.uploaded_at).getTime();
    const expire = uploaded + 7 * 24 * 3600 * 1000;
    const remaining = Math.max(0, Math.ceil((expire - Date.now()) / (24 * 3600 * 1000)));
    return remaining;
  }, [l]);
  const doDelete = async () => {
    if (!confirm(t.admin.license_delete_confirm)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/bookings/${view.data.booking_id}/license`);
      toast.success(t.admin.license_deleted);
      onDeleted && onDeleted();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setDeleting(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="license-modal">
      <div className="bg-[#0a0a0a] border border-scooter-yellow/40 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-white/10 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <IdCard className="w-5 h-5 text-scooter-yellow" />
            <h3 className="font-heading font-black text-xl tracking-tight">{t.admin.license_title}</h3>
          </div>
          <div className="flex items-center gap-3">
            {daysLeft !== null && (
              <div className={`text-[10px] tracking-[0.25em] uppercase font-bold px-3 py-1 border ${daysLeft <= 1 ? "border-red-500/40 text-red-300 bg-red-500/5" : "border-white/20 text-zinc-400"}`} data-testid="license-countdown">
                {daysLeft === 0 ? t.admin.license_expiring_today : `${t.admin.license_expires_in} ${daysLeft}g`}
              </div>
            )}
            {l && (
              <button onClick={doDelete} disabled={deleting} className="h-9 px-3 border border-red-500/40 text-red-300 text-[10px] font-bold tracking-[0.2em] uppercase hover:bg-red-500/10 disabled:opacity-50 flex items-center gap-2" data-testid="license-delete">
                {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                {t.admin.license_delete}
              </button>
            )}
            <button onClick={onClose} className="w-9 h-9 border border-white/15 hover:border-white/30 flex items-center justify-center" data-testid="license-modal-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        {view.loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-scooter-yellow" /></div>
        ) : !l ? (
          <div className="p-8 text-center text-zinc-500">{t.admin.license_none}</div>
        ) : (
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-zinc-500 text-[10px] uppercase tracking-wider block">Guidatore</span><span className="text-white font-bold">{l.driver_name}</span></div>
              <div><span className="text-zinc-500 text-[10px] uppercase tracking-wider block">Data di nascita</span><span className="text-white">{l.driver_birthdate}</span></div>
              <div><span className="text-zinc-500 text-[10px] uppercase tracking-wider block">N° patente</span><span className="text-white font-mono">{l.license_number}</span></div>
              <div><span className="text-zinc-500 text-[10px] uppercase tracking-wider block">Scadenza</span><span className="text-white">{l.license_expiry}</span></div>
              <div><span className="text-zinc-500 text-[10px] uppercase tracking-wider block">Paese</span><span className="text-white">{l.license_country}</span></div>
              <div><span className="text-zinc-500 text-[10px] uppercase tracking-wider block">Internazionale</span><span className="text-white">{l.is_international ? "Sì" : "No"}</span></div>
            </div>
            <div className="pt-4 border-t border-white/10">
              <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow mb-3">Patente nazionale</div>
              <div className="grid grid-cols-2 gap-3">
                {(l.paths?.national_front) && <LicensePhoto bookingId={view.data.booking_id} photoKey="national_front" label="Fronte nazionale" testid="license-national-front" />}
                {(l.paths?.national_back) && <LicensePhoto bookingId={view.data.booking_id} photoKey="national_back" label="Retro nazionale" testid="license-national-back" />}
              </div>
            </div>
            {l.is_international && (l.paths?.international_front || l.paths?.international_back) && (
              <div className="pt-4 border-t border-white/10">
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow mb-3">Patente internazionale</div>
                <div className="grid grid-cols-2 gap-3">
                  {(l.paths?.international_front) && <LicensePhoto bookingId={view.data.booking_id} photoKey="international_front" label="Fronte internazionale" testid="license-intl-front" />}
                  {(l.paths?.international_back) && <LicensePhoto bookingId={view.data.booking_id} photoKey="international_back" label="Retro internazionale" testid="license-intl-back" />}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Fetches a license photo as an authenticated blob (image bytes are proxied by the backend from Object Storage).
function LicensePhoto({ bookingId, photoKey, label, testid }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/admin/bookings/${bookingId}/license/photo/${photoKey}`, { responseType: "blob" });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setErr(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [bookingId, photoKey]);
  if (err) return <div className="w-full h-32 border border-red-500/30 bg-red-500/5 flex items-center justify-center text-red-300 text-xs" data-testid={`${testid}-error`}>Errore caricamento</div>;
  if (!url) return <div className="w-full h-32 border border-white/10 bg-white/[0.02] flex items-center justify-center" data-testid={`${testid}-loading`}><Loader2 className="w-4 h-4 animate-spin text-zinc-500" /></div>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-link`}>
      <img src={url} alt={label} className="w-full border border-white/10" data-testid={`${testid}-img`} />
    </a>
  );
}

// Simple beep synthesized via WebAudio (no asset dependency)
function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = "sine";
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
    osc.start();
    osc.stop(ctx.currentTime + 0.5);
    setTimeout(() => ctx.close(), 700);
  } catch (e) {
    console.warn("notification beep unavailable:", e?.message);
  }
}

function NotificationBell({ t, onNewBooking }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [lastCheck, setLastCheck] = useState(() => new Date().toISOString());
  const [enabled, setEnabled] = useState(() => typeof Notification !== "undefined" && Notification.permission === "granted");

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/admin/notifications`, { params: { since: lastCheck } });
        if (cancelled) return;
        const newOnes = (data.bookings || []).filter(b => (b.paid_at || b.created_at) > lastCheck);
        if (newOnes.length > 0) {
          setItems(prev => [...newOnes, ...prev].slice(0, 20));
          playBeep();
          toast.success(t.admin.notif_new + ` (${newOnes.length})`, {
            description: newOnes[0].customer_name + " · €" + (newOnes[0].total_price || 0).toFixed(0),
          });
          if (enabled && typeof Notification !== "undefined") {
            try {
              new Notification("Vico's Scooter — " + t.admin.notif_new, {
                body: `${newOnes[0].customer_name} · €${(newOnes[0].total_price || 0).toFixed(0)}`,
                icon: "/logo192.png",
                silent: false,
              });
            } catch (e) { console.warn("desktop notification failed:", e?.message); }
          }
          onNewBooking && onNewBooking();
        }
        if (data.server_time) setLastCheck(data.server_time);
      } catch (e) { console.warn("admin notifications poll failed:", e?.message); }
    };
    poll();  // immediate
    const id = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [lastCheck, enabled, t.admin.notif_new, onNewBooking]);

  const requestPerm = async () => {
    if (typeof Notification === "undefined") return;
    const p = await Notification.requestPermission();
    setEnabled(p === "granted");
    if (p === "granted") toast.success(t.admin.notif_enabled);
  };

  const clear = () => { setItems([]); setOpen(false); };

  return (
    <div className="relative" data-testid="notif-bell-wrapper">
      <button onClick={() => setOpen(o => !o)} className="relative h-11 w-11 border border-white/15 hover:border-scooter-yellow flex items-center justify-center transition-colors" data-testid="notif-bell">
        {items.length > 0 ? <BellRing className="w-4 h-4 text-scooter-yellow animate-pulse" /> : <Bell className="w-4 h-4 text-zinc-400" />}
        {items.length > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full" data-testid="notif-count">{items.length}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-14 w-80 border border-white/15 bg-[#0a0a0a] shadow-2xl z-50" data-testid="notif-panel">
          <div className="p-3 border-b border-white/10 flex items-center justify-between">
            <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-scooter-yellow">{t.admin.notif_title}</span>
            <button onClick={clear} className="text-[10px] text-zinc-400 hover:text-white uppercase tracking-wider" data-testid="notif-clear">{t.admin.notif_clear}</button>
          </div>
          {!enabled && typeof Notification !== "undefined" && (
            <button onClick={requestPerm} className="w-full text-left px-3 py-2 text-[11px] text-scooter-yellow hover:bg-white/5 border-b border-white/10 flex items-center gap-2" data-testid="notif-enable">
              <BellRing className="w-3 h-3" /> {t.admin.notif_enable}
            </button>
          )}
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-xs text-zinc-500" data-testid="notif-empty">{t.admin.notif_empty}</div>
            ) : items.map(b => (
              <div key={b.booking_id} className="p-3 border-b border-white/5 hover:bg-white/5" data-testid={`notif-item-${b.booking_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-bold text-white truncate">{b.customer_name}</span>
                  <span className="text-scooter-yellow font-bold text-sm">€{(b.total_price || 0).toFixed(0)}</span>
                </div>
                <div className="text-[11px] text-zinc-400 mt-1">{b.start_date} → {b.end_date} · {b.quantity}× · {b.status}</div>
                <div className="text-[10px] text-zinc-500 mt-1 truncate">{b.customer_email}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function AdminGalleryPanel({ t }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = () => api.get("/gallery").then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error(t.admin.gallery_too_big || "Immagine troppo grande (max 8 MB)");
    const reader = new FileReader();
    reader.onload = () => { setFile(reader.result); setPreview(reader.result); };
    reader.readAsDataURL(f);
  };

  const add = async () => {
    if (!file) return toast.error(t.admin.gallery_pick_photo || "Scegli una foto");
    setSaving(true);
    try {
      await api.post("/admin/gallery", { image: file, caption });
      setFile(null); setPreview(""); setCaption("");
      toast.success(t.admin.gallery_added || "Foto aggiunta");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const updateCaption = async (id, val) => {
    try {
      await api.patch(`/admin/gallery/${id}`, { caption: val });
      setItems(prev => prev.map(x => x.item_id === id ? { ...x, caption: val } : x));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t.admin.confirm_delete)) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success("Eliminata");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="bg-[#0a0a0a] border border-white/10 p-6 lg:col-span-2" data-testid="admin-gallery-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">{t.admin.gallery_title || "Galleria pubblica"}</div>
          <div className="text-xs text-zinc-500 mt-1">{t.admin.gallery_sub || "Le foto compariranno nella sezione Galleria della homepage."}</div>
        </div>
      </div>
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-scooter-yellow" />
      ) : items.length === 0 ? (
        <div className="text-xs text-zinc-500 border border-white/10 p-4" data-testid="gallery-empty">
          {t.admin.gallery_empty || "Nessuna foto ancora. Aggiungine una qui sotto."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(it => (
            <div key={it.item_id} className="border border-white/10 flex flex-col" data-testid={`admin-gallery-${it.item_id}`}>
              <div className="aspect-[4/3] bg-black overflow-hidden">
                <img src={`${backend}/api/gallery/${it.item_id}/image`} alt={it.caption || ""} className="w-full h-full object-cover" />
              </div>
              <div className="p-3 flex-1 flex flex-col gap-2">
                <Textarea
                  defaultValue={it.caption || ""}
                  onBlur={(e) => e.target.value !== (it.caption || "") && updateCaption(it.item_id, e.target.value)}
                  placeholder={t.admin.gallery_caption_ph || "Aggiungi un commento…"}
                  className="bg-transparent border-white/15 rounded-none text-white text-sm min-h-[70px] focus-visible:ring-scooter-yellow"
                  data-testid={`gallery-caption-${it.item_id}`}
                />
                <button onClick={() => remove(it.item_id)} className="self-end px-3 py-1.5 border border-white/10 text-zinc-400 hover:text-red-400 hover:bg-white/5 text-[10px] tracking-[0.2em] uppercase font-bold flex items-center gap-2" data-testid={`gallery-delete-${it.item_id}`}>
                  <Trash2 className="w-3 h-3" />{t.admin.delete}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-white/10 pt-6 mt-6 space-y-3">
        <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-scooter-yellow">{t.admin.gallery_add_title || "Aggiungi foto"}</div>
        <input type="file" accept="image/*" onChange={onFile} className="text-xs text-zinc-300 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-scooter-yellow file:text-black file:font-bold file:text-[10px] file:tracking-[0.2em] file:uppercase file:cursor-pointer cursor-pointer" data-testid="gallery-file-input" />
        {preview && (
          <div className="w-40 h-30 border border-white/10 overflow-hidden">
            <img src={preview} alt="preview" className="w-full h-full object-cover" />
          </div>
        )}
        <Textarea value={caption} onChange={e => setCaption(e.target.value)} placeholder={t.admin.gallery_caption_ph || "Commento sotto la foto (facoltativo)"} className="bg-transparent border-white/15 rounded-none text-white min-h-[80px] focus-visible:ring-scooter-yellow" data-testid="gallery-caption-input" />
        <button onClick={add} disabled={saving || !file} className="h-11 px-6 bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover disabled:opacity-50 flex items-center gap-2" data-testid="gallery-add-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {t.admin.gallery_add || "Aggiungi alla galleria"}
        </button>
      </div>
    </div>
  );
}


function PushNotificationsPanel({ t }) {
  const [supported, setSupported] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSupported(isPushSupported());
    if (!isPushSupported()) return;
    getCurrentSubscription().then(s => setSubscribed(!!s)).catch(() => setSubscribed(false));
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeAdminForPush();
      setSubscribed(true);
      setPermission(Notification.permission);
      toast.success(t.admin.push_enabled || "Notifiche push attive su questo dispositivo");
    } catch (e) { toast.error(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeAdminFromPush();
      setSubscribed(false);
      toast.success(t.admin.push_disabled || "Notifiche push disattivate");
    } catch (e) { toast.error(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await sendPushTest();
      if (r?.disabled) toast.error("VAPID non configurato lato server");
      else toast.success(`${t.admin.push_test_sent || "Notifica di test inviata"} (${r?.sent || 0})`);
    } catch (e) { toast.error(e.response?.data?.detail || String(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-8" data-testid="push-panel">
      <div className="flex items-center gap-2 mb-1">
        <BellDot className="w-4 h-4 text-scooter-yellow" />
        <h2 className="font-heading font-black text-xl tracking-tight">{t.admin.push_title || "Notifiche push sul telefono"}</h2>
      </div>
      <p className="text-xs text-zinc-400 mb-4">{t.admin.push_sub || "Attivale su questo dispositivo per ricevere una notifica appena arriva una nuova prenotazione o un pagamento."}</p>
      {!supported ? (
        <div className="border border-white/10 p-5 text-xs text-zinc-300" data-testid="push-unsupported">
          {t.admin.push_unsupported || "Il tuo browser non supporta le notifiche push. Su iPhone aggiungi il sito alla schermata Home (Safari → Condividi → Aggiungi a Home), poi riapri l'app dalla Home e riprova."}
        </div>
      ) : (
        <div className="border border-white/10 p-5 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-zinc-400">
              {t.admin.push_status || "Stato su questo dispositivo"}:{" "}
              <span className={subscribed ? "text-scooter-yellow font-bold" : "text-zinc-300 font-bold"} data-testid="push-status">
                {subscribed ? (t.admin.push_on || "ATTIVE") : (t.admin.push_off || "DISATTIVE")}
              </span>
              {permission === "denied" && (
                <span className="ml-2 text-red-400">{t.admin.push_denied || "· permesso bloccato dal browser"}</span>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {!subscribed ? (
                <button onClick={enable} disabled={busy || permission === "denied"} className="h-11 px-5 bg-scooter-yellow text-black font-bold text-xs tracking-[0.2em] uppercase hover:bg-scooter-yellow-hover disabled:opacity-50 flex items-center gap-2" data-testid="push-enable-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellDot className="w-4 h-4" />}
                  {t.admin.push_enable || "Attiva notifiche"}
                </button>
              ) : (
                <>
                  <button onClick={test} disabled={busy} className="h-11 px-5 border border-scooter-yellow/60 text-scooter-yellow hover:bg-scooter-yellow/10 font-bold text-xs tracking-[0.2em] uppercase disabled:opacity-50 flex items-center gap-2" data-testid="push-test-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                    {t.admin.push_test || "Invia test"}
                  </button>
                  <button onClick={disable} disabled={busy} className="h-11 px-5 border border-white/20 text-zinc-300 hover:bg-white/5 font-bold text-xs tracking-[0.2em] uppercase disabled:opacity-50 flex items-center gap-2" data-testid="push-disable-btn">
                    {t.admin.push_disable || "Disattiva"}
                  </button>
                </>
              )}
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            {t.admin.push_hint || "Su iPhone (iOS 16.4+): aggiungi vico-scooter.it alla schermata Home (Safari → Condividi → \"Aggiungi a Home\"), apri l'app dalla Home e clicca Attiva. Su Android: funziona direttamente da Chrome/Edge/Firefox."}
          </p>
        </div>
      )}
    </div>
  );
}

